import type { PhotoMetadata } from '../types/photo';

export class ExifService {
  /**
   * Extract EXIF metadata from image file
   * Note: This is a simplified implementation. In production, use a library like 'exif-js' or 'piexifjs'
   */
  async extractExif(file: File): Promise<PhotoMetadata> {
    // For now, we'll return basic metadata
    // In a real implementation, you would use a library to extract EXIF data

    const metadata: PhotoMetadata = {};

    try {
      // Try to extract GPS coordinates from EXIF if available
      // This is a placeholder - actual EXIF extraction would require a library

      // Get file timestamp as fallback for date_time_original
      metadata.date_time_original = new Date(file.lastModified).toISOString();

    } catch (error) {
      console.error('Error extracting EXIF data:', error);
    }

    return metadata;
  }

  /**
   * Convert EXIF GPS format to decimal degrees
   * EXIF stores GPS as degrees, minutes, seconds
   * @private - Reserved for future EXIF GPS parsing
   * @deprecated Not currently used - reserved for future implementation
   */
  // @ts-ignore - Reserved for future use
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private _convertGPSToDecimal(
    _degrees: number,
    _minutes: number,
    _seconds: number,
    _direction: 'N' | 'S' | 'E' | 'W'
  ): number {
    let decimal = _degrees + _minutes / 60 + _seconds / 3600;

    if (_direction === 'S' || _direction === 'W') {
      decimal = -decimal;
    }

    return decimal;
  }

  /**
   * Read EXIF orientation tag
   */
  async getOrientation(_file: File): Promise<number> {
    // Default orientation (no rotation)
    return 1;
  }

  /**
   * Check if file has EXIF data
   */
  async hasExif(file: File): Promise<boolean> {
    // Simplified check - in production, read file header
    return file.type === 'image/jpeg' || file.type === 'image/jpg';
  }

  /**
   * Strip EXIF data from image (for privacy)
   */
  async stripExif(file: File): Promise<File> {
    // This would require reading the file, removing EXIF markers, and returning a new file
    // For now, just return the original file
    // In production, use a library like 'piexifjs'
    return file;
  }
}

// Export singleton instance
export const exifService = new ExifService();
